import { useEffect, useRef, useState } from "react";
import {
  formatDob,
  getAllowedMonthsForDay,
  getAllowedYearsForDayMonth,
  getInitialDayOptions,
  parseDob,
  MONTH_ABBR,
} from "../../utils/dob";

type Segment = "day" | "month" | "year";

interface DateOfBirthPickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  titleColor?: string;
  backgroundColor?: string;
}

const CalendarIcon = () => (
  <svg
    className="w-4 h-4 text-text-subtle shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

function DateOfBirthPicker({
  value,
  onChange,
  disabled = false,
  titleColor = "text-text-subtle",
  backgroundColor = "bg-bg-page-body",
}: DateOfBirthPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const pendingFocus = useRef<Segment | null>(null);
  const [openSegment, setOpenSegment] = useState<Segment | null>(null);
  const [activeSegment, setActiveSegment] = useState<Segment | null>(null);
  const [dayDraft, setDayDraft] = useState("");
  const [monthDraft, setMonthDraft] = useState("");
  const [yearDraft, setYearDraft] = useState("");
  const [focusTick, setFocusTick] = useState(0);

  const parsed = value ? parseDob(value) : null;
  const [day, setDay] = useState<number | null>(parsed?.day ?? null);
  const [month, setMonth] = useState<number | null>(parsed?.month ?? null);
  const [year, setYear] = useState<number | null>(parsed?.year ?? null);

  useEffect(() => {
    const next = value ? parseDob(value) : null;
    setDay(next?.day ?? null);
    setMonth(next?.month ?? null);
    setYear(next?.year ?? null);
  }, [value]);

  useEffect(() => {
    if (disabled) setOpenSegment(null);
  }, [disabled]);

  useEffect(() => {
    if (!pendingFocus.current) return;
    const segment = pendingFocus.current;
    pendingFocus.current = null;
    const target =
      segment === "day"
        ? dayRef.current
        : segment === "month"
          ? monthRef.current
          : yearRef.current;
    target?.focus();
  }, [focusTick]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpenSegment(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const emitChange = (
    nextDay: number | null,
    nextMonth: number | null,
    nextYear: number | null,
  ) => {
    if (nextDay && nextMonth && nextYear) {
      onChange(formatDob(nextYear, nextMonth, nextDay));
      return;
    }
    onChange("");
  };

  const getCurrentStep = (): Segment => {
    if (openSegment) return openSegment;
    if (!day) return "day";
    if (!month) return "month";
    return "year";
  };

  const canOpen = (segment: Segment) => {
    if (disabled) return false;
    if (segment === "month" && !day) return false;
    if (segment === "year" && (!day || !month)) return false;
    return true;
  };

  const revealSegment = (segment: Segment) => {
    if (!canOpen(segment)) return;
    setOpenSegment(segment);
  };

  const openOnly = (segment: Segment) => {
    if (!canOpen(segment)) return;
    setOpenSegment((current) => (current === segment ? null : segment));
  };

  const openCurrentStep = () => openOnly(getCurrentStep());

  const focusNext = (segment: Segment) => {
    pendingFocus.current = segment;
    setActiveSegment(segment);
    setFocusTick((tick) => tick + 1);
  };

  const handleDaySelect = (d: number, advance = false) => {
    let nextMonth = month;
    let nextYear = year;

    if (nextMonth && !getAllowedMonthsForDay(d).includes(nextMonth)) {
      nextMonth = null;
      nextYear = null;
    } else if (nextMonth && nextYear) {
      const allowedYears = getAllowedYearsForDayMonth(d, nextMonth);
      if (!allowedYears.includes(nextYear)) nextYear = null;
    }

    setDay(d);
    setMonth(nextMonth);
    setYear(nextYear);
    setDayDraft("");
    setOpenSegment(null);
    emitChange(d, nextMonth, nextYear);
    if (advance) focusNext("month");
  };

  const handleMonthSelect = (m: number, advance = false) => {
    if (!day) return;

    let nextYear = year;
    if (nextYear) {
      const allowedYears = getAllowedYearsForDayMonth(day, m);
      if (!allowedYears.includes(nextYear)) nextYear = null;
    }

    setMonth(m);
    setYear(nextYear);
    setMonthDraft("");
    setOpenSegment(null);
    emitChange(day, m, nextYear);
    if (advance) focusNext("year");
  };

  const handleYearSelect = (y: number) => {
    if (!day || !month) return;
    setYear(y);
    setYearDraft("");
    setOpenSegment(null);
    setActiveSegment(null);
    emitChange(day, month, y);
  };

  const dayOptions = getInitialDayOptions();
  const monthOptions = day ? getAllowedMonthsForDay(day) : [];
  const yearOptions =
    day && month ? getAllowedYearsForDayMonth(day, month) : [];

  const dayText = day ? String(day).padStart(2, "0") : "";
  const monthText = month ? MONTH_ABBR[month - 1] : "";
  const yearText = year ? String(year) : "";

  const isSegmentSelected = (segment: Segment, optionValue: number) => {
    if (segment === "day") return day === optionValue;
    if (segment === "month") return month === optionValue;
    return year === optionValue;
  };

  const dropdownOptions =
    openSegment === "day"
      ? dayOptions.map((d) => ({
          value: d,
          label: String(d).padStart(2, "0"),
        }))
      : openSegment === "month"
        ? monthOptions.map((m) => ({
            value: m,
            label: MONTH_ABBR[m - 1],
          }))
        : openSegment === "year"
          ? yearOptions.map((y) => ({ value: y, label: String(y) }))
          : [];

  const handleOptionSelect = (val: number) => {
    if (openSegment === "day") handleDaySelect(val, true);
    else if (openSegment === "month") handleMonthSelect(val, true);
    else if (openSegment === "year") handleYearSelect(val);
  };

  const commitDay = (digits: string) => {
    const parsedDay = Number(digits);
    if (!digits || parsedDay < 1 || parsedDay > 31) return false;
    handleDaySelect(parsedDay, true);
    return true;
  };

  const handleDayInput = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 2);
    if (digits.length === 2 || (digits.length === 1 && Number(digits) >= 4)) {
      if (commitDay(digits)) return;
      if (digits.length === 2) {
        setDayDraft(digits.slice(0, 1));
        setOpenSegment(null);
        return;
      }
    }
    setDayDraft(digits);
    setOpenSegment(null);
  };

  const handleMonthInput = (raw: string) => {
    const letters = raw.replace(/[^a-zA-Z]/g, "").slice(0, 3);
    const prefix = letters.toLowerCase();
    const matches = prefix
      ? monthOptions.filter((m) =>
          MONTH_ABBR[m - 1].toLowerCase().startsWith(prefix),
        )
      : [];
    if (prefix && matches.length === 0) return;
    if (matches.length === 1) {
      handleMonthSelect(matches[0], true);
      return;
    }
    const pretty = letters
      ? letters.charAt(0).toUpperCase() + letters.slice(1).toLowerCase()
      : "";
    setMonthDraft(pretty);
    setOpenSegment(null);
  };

  const handleYearInput = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    if (digits.length === 4 && day && month) {
      const parsedYear = Number(digits);
      if (getAllowedYearsForDayMonth(day, month).includes(parsedYear)) {
        handleYearSelect(parsedYear);
        return;
      }
    }
    setYearDraft(digits);
    setOpenSegment(null);
  };

  const focusSegment = (segment: Segment) => {
    if (segment === "day") dayRef.current?.focus();
    if (segment === "month") monthRef.current?.focus();
    if (segment === "year") yearRef.current?.focus();
  };

  const isOpen = openSegment !== null && dropdownOptions.length > 0;

  return (
    <div className="w-full min-w-0 flex flex-col" ref={containerRef}>
      <span className={`${titleColor} text-[10px] font-normal`}>
        Date of Birth
      </span>
      <div className="my-[3px]" />

      <div className="relative">
        <div
          className={[
            "flex items-center justify-between gap-1 w-full px-2 py-[8px] border border-border-subtle rounded-lg",
            disabled
              ? "bg-bg-disabled opacity-70 pointer-events-none cursor-default"
              : `${backgroundColor} cursor-pointer`,
          ].join(" ")}
          aria-disabled={disabled}
        >
          <div className="flex items-center min-w-0">
            <input
              ref={dayRef}
              type="text"
              inputMode="numeric"
              aria-label="Day"
              placeholder="dd"
              disabled={disabled}
              value={activeSegment === "day" ? dayDraft : dayText}
              onMouseDown={() => revealSegment("day")}
              onClick={() => revealSegment("day")}
              onFocus={(e) => {
                setActiveSegment("day");
                setDayDraft(day ? String(day).padStart(2, "0") : "");
                revealSegment("day");
                requestAnimationFrame(() => e.target.select());
              }}
              onBlur={(e) => {
                const digits = e.currentTarget.value.replace(/\D/g, "");
                if (digits.length === 1 && Number(digits) >= 1) commitDay(digits);
                setActiveSegment((current) => (current === "day" ? null : current));
              }}
              onChange={(e) => handleDayInput(e.target.value)}
              className="w-[1.6rem] bg-transparent border-0 p-0 text-[12px] font-normal text-text-base placeholder:text-border-input-dark-bg focus:outline-none"
            />
            <span className="text-border-input-dark-bg mx-0.5 text-[12px]">
              /
            </span>
            <input
              ref={monthRef}
              type="text"
              aria-label="Month"
              placeholder="mmm"
              disabled={disabled || !day}
              value={activeSegment === "month" ? monthDraft : monthText}
              onMouseDown={() => revealSegment("month")}
              onClick={() => revealSegment("month")}
              onFocus={(e) => {
                if (!day) return;
                setActiveSegment("month");
                setMonthDraft(month ? MONTH_ABBR[month - 1] : "");
                revealSegment("month");
                requestAnimationFrame(() => e.target.select());
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !e.currentTarget.value) {
                  e.preventDefault();
                  focusSegment("day");
                }
              }}
              onBlur={() => {
                setActiveSegment((current) =>
                  current === "month" ? null : current,
                );
              }}
              onChange={(e) => handleMonthInput(e.target.value)}
              className="w-[2.2rem] bg-transparent border-0 p-0 text-[12px] font-normal text-text-base placeholder:text-border-input-dark-bg focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-border-input-dark-bg mx-0.5 text-[12px]">
              /
            </span>
            <input
              ref={yearRef}
              type="text"
              inputMode="numeric"
              aria-label="Year"
              placeholder="yyyy"
              disabled={disabled || !day || !month}
              value={activeSegment === "year" ? yearDraft : yearText}
              onMouseDown={() => revealSegment("year")}
              onClick={() => revealSegment("year")}
              onFocus={(e) => {
                if (!day || !month) return;
                setActiveSegment("year");
                setYearDraft(year ? String(year) : "");
                revealSegment("year");
                requestAnimationFrame(() => e.target.select());
              }}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !e.currentTarget.value) {
                  e.preventDefault();
                  focusSegment("month");
                }
              }}
              onBlur={() => {
                setActiveSegment((current) =>
                  current === "year" ? null : current,
                );
              }}
              onChange={(e) => handleYearInput(e.target.value)}
              className="w-[2.6rem] bg-transparent border-0 p-0 text-[12px] font-normal text-text-base placeholder:text-border-input-dark-bg focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <button
            type="button"
            className="shrink-0 flex items-center cursor-pointer disabled:cursor-not-allowed"
            onClick={openCurrentStep}
            aria-label={`Select ${getCurrentStep()}`}
            disabled={disabled}
          >
            <CalendarIcon />
          </button>
        </div>

        {isOpen && openSegment ? (
          <div className="absolute z-[9999] left-0 right-0 mt-1 overflow-hidden rounded-lg border border-border-subtle bg-bg-page-body shadow-lg">
            <div className="max-h-40 overflow-y-auto scrollbar-translucent py-1">
              {dropdownOptions.map((opt) => {
                const selected = isSegmentSelected(openSegment, opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={[
                      "w-full px-2 py-[6px] text-left text-[12px] font-normal cursor-pointer",
                      selected
                        ? "bg-bg-base text-text-base font-normal"
                        : "text-text-base hover:bg-bg-base",
                    ].join(" ")}
                    onClick={() => handleOptionSelect(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default DateOfBirthPicker;
